import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, FileText, Users, Download, Upload, Mail } from "lucide-react";
import { api, API } from "../lib/api";
import { useLang } from "../context/LangContext";

const Field = ({ label, value, onChange, textarea }) => (
  <div className="field">
    <label className="label-caps block mb-2">{label}</label>
    {textarea ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

function Login({ onLogin }) {
  const { t } = useLang();
  const a = t.admin;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      onLogin();
    } catch {
      toast.error(a.loginError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login" className="w-full max-w-sm">
        <div className="font-serif text-3xl text-cream mb-2">{a.title}</div>
        <div className="overline mb-10">{a.subtitle}</div>
        <div className="field mb-8">
          <label className="label-caps block mb-2">{a.email}</label>
          <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field mb-10">
          <label className="label-caps block mb-2">{a.password}</label>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <button data-testid="admin-login-submit" disabled={loading} className="btn-gold btn-caps w-full justify-center">{loading ? "…" : a.login}</button>
      </form>
    </div>
  );
}

function HeroEditor({ a, content, setHero, setHeroLine }) {
  return ["pt", "en"].map((lang) => (
    <div key={lang} className="space-y-6">
      <div className="overline">{a.heroTitle} — {lang.toUpperCase()}</div>
      <Field label={a.overline} value={content.hero[lang].overline} onChange={(v) => setHero(lang, "overline", v)} />
      <div className="grid md:grid-cols-3 gap-6">
        {content.hero[lang].titleLines.map((ln, idx) => (
          <Field key={`${lang}-line-${idx}`} label={`Linha ${idx + 1}`} value={ln} onChange={(v) => setHeroLine(lang, idx, v)} />
        ))}
      </div>
      <Field label={a.heroSub} value={content.hero[lang].subtitle} onChange={(v) => setHero(lang, "subtitle", v)} textarea />
    </div>
  ));
}

function AreasEditor({ a, content, setArea }) {
  return (
    <div className="space-y-6">
      <div className="overline">{a.areasTitle}</div>
      {content.practiceAreas.map((ar, i) => (
        <div key={ar.id} className="p-6 border border-ink-700 grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Field label={`PT · ${ar.num} título`} value={ar.pt.title} onChange={(v) => setArea(i, "pt", "title", v)} />
            <Field label="PT descrição" value={ar.pt.desc} onChange={(v) => setArea(i, "pt", "desc", v)} textarea />
          </div>
          <div className="space-y-4">
            <Field label={`EN · ${ar.num} title`} value={ar.en.title} onChange={(v) => setArea(i, "en", "title", v)} />
            <Field label="EN description" value={ar.en.desc} onChange={(v) => setArea(i, "en", "desc", v)} textarea />
          </div>
        </div>
      ))}
    </div>
  );
}

function GuideEditor({ a, content, setGuide }) {
  return (
    <div className="space-y-6">
      <div className="overline">{a.guideTitle}</div>
      <div className="grid md:grid-cols-2 gap-6">
        {["pt", "en"].map((lang) => (
          <div key={lang} className="space-y-4">
            <Field label={`${lang.toUpperCase()} título`} value={content.guide[lang].title} onChange={(v) => setGuide(lang, "title", v)} />
            <Field label={`${lang.toUpperCase()} descrição`} value={content.guide[lang].desc} onChange={(v) => setGuide(lang, "desc", v)} textarea />
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentTab({ a, content, setContent }) {
  const setHero = (lang, key, val) => setContent({ ...content, hero: { ...content.hero, [lang]: { ...content.hero[lang], [key]: val } } });
  const setHeroLine = (lang, idx, val) => {
    const lines = [...content.hero[lang].titleLines];
    lines[idx] = val;
    setHero(lang, "titleLines", lines);
  };
  const setArea = (i, lang, key, val) => {
    const areas = content.practiceAreas.map((ar, j) => j === i ? { ...ar, [lang]: { ...ar[lang], [key]: val } } : ar);
    setContent({ ...content, practiceAreas: areas });
  };
  const setGuide = (lang, key, val) => setContent({ ...content, guide: { ...content.guide, [lang]: { ...content.guide[lang], [key]: val } } });

  const saveContent = async () => {
    try {
      await api.put("/admin/content", { hero: content.hero, practiceAreas: content.practiceAreas, guide: content.guide });
      toast.success(a.saved);
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-14 max-w-4xl">
      <HeroEditor a={a} content={content} setHero={setHero} setHeroLine={setHeroLine} />
      <AreasEditor a={a} content={content} setArea={setArea} />
      <GuideEditor a={a} content={content} setGuide={setGuide} />
      <button data-testid="admin-save" onClick={saveContent} className="btn-solid btn-caps">{a.save}</button>
    </div>
  );
}

function LeadsTab({ a, leads }) {
  const exportCsv = () => window.open(`${API}/admin/leads.csv`, "_blank");
  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="text-sand">{leads.length} {a.leadsCount}</div>
        <button data-testid="admin-export-csv" onClick={exportCsv} className="btn-gold btn-caps flex items-center gap-2"><Download size={14} /> {a.exportCsv}</button>
      </div>
      {leads.length === 0 ? <p className="text-sand">{a.noLeads}</p> : (
        <div className="border border-ink-700 divide-y divide-ink-700">
          {leads.map((l) => (
            <div key={`${l.email}-${l.created_at}`} className="grid grid-cols-4 gap-4 p-4 text-sm">
              <span className="text-cream">{l.email}</span>
              <span className="text-sand">{l.name || "—"}</span>
              <span className="text-gold uppercase">{l.lang}</span>
              <span className="text-sand text-xs">{new Date(l.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesTab({ a, messages }) {
  return (
    <div>
      <div className="text-sand mb-8">{messages.length} {a.messagesCount}</div>
      {messages.length === 0 ? <p className="text-sand">{a.noMessages}</p> : (
        <div className="space-y-4">
          {messages.map((m) => (
            <div key={`${m.email}-${m.created_at}`} data-testid="admin-message-card" className="p-6 border border-ink-700 bg-ink-800/40">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-cream font-serif text-lg">{m.name}</span>
                  <span className="text-gold uppercase text-xs ml-3">{m.lang}</span>
                </div>
                <span className="text-sand text-xs">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sand text-sm mt-2">
                <a href={`mailto:${m.email}`} className="text-gold hover:underline">{m.email}</a>
                {m.phone ? <span className="ml-4">{m.phone}</span> : null}
              </div>
              {m.subject ? <div className="text-cream text-sm mt-3 font-medium">{m.subject}</div> : null}
              <p className="text-sand text-sm leading-relaxed mt-2 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PdfTab({ a }) {
  const uploadPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/guide-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(a.uploaded);
    } catch { toast.error("Error"); }
  };
  return (
    <div className="max-w-md">
      <div className="overline mb-6">{a.uploadPdf}</div>
      <p className="text-sand text-sm mb-6">PDF · {a.tabs.pdf}</p>
      <label className="btn-gold btn-caps inline-flex cursor-pointer">
        <Upload size={14} /> {a.upload}
        <input data-testid="admin-pdf-upload" type="file" accept="application/pdf" onChange={uploadPdf} className="hidden" />
      </label>
      <a href={`${API}/guide/pdf`} target="_blank" rel="noreferrer" className="block mt-6 link-underline text-gold btn-caps">Preview PDF</a>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const { t } = useLang();
  const a = t.admin;
  const [tab, setTab] = useState("content");
  const [content, setContent] = useState(null);
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data.leads)).catch(() => {});
    api.get("/admin/messages").then((r) => setMessages(r.data.messages)).catch(() => {});
  }, []);

  const tabs = [
    { id: "content", label: a.tabs.content, icon: FileText },
    { id: "leads", label: `${a.tabs.leads} (${leads.length})`, icon: Users },
    { id: "messages", label: `${a.tabs.messages} (${messages.length})`, icon: Mail },
    { id: "pdf", label: a.tabs.pdf, icon: Upload },
  ];

  return (
    <div className="min-h-screen bg-ink">
      <header className="border-b border-ink-800 sticky top-0 bg-ink/90 backdrop-blur-xl z-10">
        <div className="container-lux flex items-center justify-between h-16">
          <div className="font-serif text-xl text-cream">{a.title}</div>
          <button data-testid="admin-logout" onClick={onLogout} className="btn-caps text-sand hover:text-gold flex items-center gap-2"><LogOut size={14} /> {a.logout}</button>
        </div>
      </header>

      <div className="container-lux py-12">
        <div className="flex gap-2 mb-12 flex-wrap">
          {tabs.map((tb) => (
            <button key={tb.id} data-testid={`admin-tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`btn-caps px-5 py-3 border transition-colors flex items-center gap-2 ${tab === tb.id ? "border-gold text-gold" : "border-ink-700 text-sand hover:text-cream"}`}>
              <tb.icon size={14} /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "content" && content && <ContentTab a={a} content={content} setContent={setContent} />}
        {tab === "leads" && <LeadsTab a={a} leads={leads} />}
        {tab === "messages" && <MessagesTab a={a} messages={messages} />}
        {tab === "pdf" && <PdfTab a={a} />}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    setAuthed(false);
  };

  if (authed === null) return <div className="min-h-screen bg-ink" />;
  return authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
